import { Link } from "react-router-dom";
import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { useAuth } from "../context/AuthContext";
import NotifyMeModal from "../components/NotifyMeModal";
import { usePublicPageMeta } from "../hooks/usePageMeta";
import { BARN_ICON_URL, CARROT_ICON_URL } from "../lib/mapIcons";

const HomePreviewMap = lazy(() => import("../components/HomePreviewMap"));

function MemberPreviewInvitation() {
  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center px-5 pointer-events-none">
      <Link
        to="/signup?redirect=%2Fdashboard"
        data-testid="member-preview-invitation"
        className="pointer-events-auto block w-full max-w-[196px] border text-center shadow-md transition-shadow hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0f1d2f] focus-visible:ring-offset-4 px-3 py-3.5"
        style={{ color: '#0f1d2f', borderColor: '#a28a4f', background: 'linear-gradient(135deg, #f4ecd4 0%, #d8c493 100%)', borderRadius: '3px', textDecoration: 'none' }}
      >
        <span className="block text-[9px] tracking-[0.18em] uppercase">Member Preview</span>
        <span className="block text-sm leading-snug mt-1.5">Your Farmstand experience begins here</span>
        <span className="block text-[10px] tracking-[0.08em] uppercase mt-2">
          Create Your Free Account <span aria-hidden="true">→</span>
        </span>
      </Link>
    </div>
  );
}

const LOGO_URL = "/images/optimized/logo-v1.webp";

// Hero background — latest cinematic image
const HERO_BG = "/images/optimized/hero-v4.webp";

// Editorial watercolor illustrations - heritage-forward aesthetic
const IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_farm-connect-44/artifacts/6ufbl5gv_IMG_3496.png",
  hands: "https://customer-assets.emergentagent.com/job_farm-market-27/artifacts/p8nukd8d_12A11A56-8A62-4BC5-A040-6531041DAAE5.jpeg",
  barn: "https://customer-assets.emergentagent.com/job_farm-market-27/artifacts/k2cmvhmg_IMG_1465.jpeg",
  landscape: "https://customer-assets.emergentagent.com/job_farm-market-27/artifacts/8smt5tyg_51D730AB-8716-4248-80FD-F62DC13F112D.jpeg"
};

// LATEST owner-approved closed-paper asset (small WELCOME TO, larger THE
// FARMSTAND, baked italic subline). Rendered as-is on the CLOSED trigger:
// no HTML text overlay, no CSS replacement for paper/seal/typography.
// The expanded OPEN surface is a different, CSS-generated ivory sheet
// (the ORIGINAL WaitlistCard expanded modal, with new copy/buttons).
const WELCOME_PARCHMENT_IMG = "/images/optimized/welcome-v1.webp";

/**
 * WelcomeCard — compact welcome parchment that OPENS into the ORIGINAL
 * expanded ivory sheet from the retired WaitlistCard. Closed state uses
 * the owner-supplied asset as-is (small WELCOME TO baked in). Open state
 * restores the ORIGINAL irregular ivory paper (CSS-generated: linear
 * gradient, torn clip-path edges, paper grain, edge vignette, waitlistRise
 * 0.55s easing, rotate(-1deg), max-width 460px) and renders new HTML
 * content in thin navy/charcoal serif: small WELCOME TO · THE FARMSTAND ·
 * original divider · italic subline · two CTA buttons fully inside the
 * paper with generous bottom padding. Close × tucked into the paper's
 * upper-right corner.
 *
 * Signed-in visitors see one CTA: "Explore your Farmstand" → /explore.
 *
 * Accessibility: role="dialog", aria-modal, Escape closes, focus pulled
 * to first choice on open and returned to trigger on close. Reduced-motion
 * respected (grow + rise animations suppressed).
 */
function WelcomeCard() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const triggerRef = useRef(null);
  const firstCtaRef = useRef(null);
  const reduceMotion =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => { firstCtaRef.current?.focus(); }, reduceMotion ? 0 : 420);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      clearTimeout(t);
      triggerRef.current?.focus();
    };
  }, [open, reduceMotion]);

  // Original ORIGINAL open-sheet styles reused verbatim from the retired
  // WaitlistCard — same ivory gradient, same torn clip-path, same grain,
  // same vignette, same waitlistRise easing, same rotation.
  const headingStyle = {
    fontFamily: "'GFS Didot', 'Didot', 'Playfair Display', 'Georgia', serif",
    color: "#0d2340",
    fontWeight: 600,
    letterSpacing: "2px",
    textTransform: "uppercase",
    lineHeight: "1.1",
    textAlign: "center",
  };
  const subtitleStyle = {
    fontFamily: "'Cormorant Garamond', 'Georgia', serif",
    color: "#1a2b44",
    fontStyle: "italic",
    opacity: 0.78,
    lineHeight: "1.55",
    textAlign: "center",
  };

  return (
    <>
      {/* ── Closed trigger — new asset AS-IS. Preserves original bounding
          box (clamp 180–260px, rotate -1.5deg); the img is slightly scaled
          up inside the button so the VISIBLE paper (asset has transparent
          curl/padding margins) matches the pre-change visible footprint
          without inflating the button rect. ── */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(true)}
        data-testid="welcome-card"
        aria-label="Open Welcome to the Farmstand"
        aria-expanded={open}
        aria-haspopup="dialog"
        className="parchment-card"
        style={{
          background: "transparent",
          border: "none",
          padding: 0,
          margin: 0,
          cursor: "pointer",
          display: "block",
          overflow: "visible",
          width: "clamp(180px, 17vw, 260px)",
          transform: "rotate(-1.5deg)",
          transition: reduceMotion ? "none" : "transform 0.6s cubic-bezier(0.23, 1, 0.32, 1), filter 0.6s ease",
          filter:
            "drop-shadow(0 22px 26px rgba(0,0,0,0.32)) drop-shadow(0 4px 8px rgba(0,0,0,0.14))",
          visibility: open ? "hidden" : "visible",
        }}
        onMouseEnter={(e) => {
          if (reduceMotion) return;
          e.currentTarget.style.transform = "rotate(-1.5deg) translateY(-6px) scale(1.015)";
          e.currentTarget.style.filter =
            "drop-shadow(0 32px 36px rgba(0,0,0,0.40)) drop-shadow(0 8px 14px rgba(0,0,0,0.20))";
        }}
        onMouseLeave={(e) => {
          if (reduceMotion) return;
          e.currentTarget.style.transform = "rotate(-1.5deg)";
          e.currentTarget.style.filter =
            "drop-shadow(0 22px 26px rgba(0,0,0,0.32)) drop-shadow(0 4px 8px rgba(0,0,0,0.14))";
        }}
      >
        <img
          src={WELCOME_PARCHMENT_IMG}
          alt="Welcome to The Farmstand"
          draggable={false}
          style={{
            width: "100%",
            height: "auto",
            display: "block",
            userSelect: "none",
            pointerEvents: "none",
          }}
        />
      </button>

      {/* ── Opened state: ORIGINAL irregular ivory sheet (CSS). Different
          from the closed asset, matching original expanded WaitlistCard
          size (max-width 460px) and open animation (waitlistRise 0.55s). ── */}
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="welcome-dialog-title"
          data-testid="welcome-dialog"
          onClick={() => setOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            background: "rgba(8,12,22,0.72)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
            animation: reduceMotion ? "none" : "waitlistFadeIn 0.35s ease-out",
          }}
        >
          <style>{`
            @keyframes waitlistFadeIn { from { opacity: 0; } to { opacity: 1; } }
            @keyframes waitlistRise {
              from { opacity: 0; transform: translateY(24px) rotate(-1.5deg) scale(0.96); }
              to   { opacity: 1; transform: translateY(0) rotate(-1deg) scale(1); }
            }
          `}</style>

          <div
            onClick={(e) => e.stopPropagation()}
            className="waitlist-modal-card"
            style={{
              position: "relative",
              width: "100%",
              maxWidth: "460px",
              animation: reduceMotion ? "none" : "waitlistRise 0.55s cubic-bezier(0.23, 1, 0.32, 1) both",
              filter:
                "drop-shadow(0 30px 50px rgba(0,0,0,0.45)) drop-shadow(0 6px 14px rgba(0,0,0,0.20))",
              transform: "rotate(-1deg)",
            }}
          >
            {/* Parchment paper background — ORIGINAL CSS-generated ivory
                sheet with torn clip-path edges, paper grain, edge vignette. */}
            <div style={{
              position: "relative",
              background: "linear-gradient(135deg, #f1e9d6 0%, #f6efdc 35%, #f0e6cd 70%, #e6dcbe 100%)",
              clipPath: `polygon(
                0.8% 0.2%, 5% 0%, 9% 0.7%, 14% 0.1%, 19% 0.6%, 25% 0%, 31% 0.5%, 37% 0.1%, 43% 0.6%, 50% 0%, 57% 0.4%, 64% 0%, 70% 0.5%, 76% 0.1%, 82% 0.6%, 88% 0%, 93% 0.5%, 97% 0.1%, 99.5% 0.4%,
                100% 5%, 99.6% 12%, 100% 19%, 99.5% 26%, 100% 33%, 99.6% 41%, 100% 48%, 99.5% 56%, 100% 63%, 99.6% 70%, 100% 78%, 99.5% 85%, 100% 92%, 99.6% 97%,
                99.5% 100%, 95% 99.5%, 90% 100%, 85% 99.6%, 79% 100%, 73% 99.5%, 67% 100%, 61% 99.6%, 55% 100%, 49% 99.5%, 43% 100%, 37% 99.6%, 31% 100%, 25% 99.5%, 19% 100%, 13% 99.6%, 7% 100%, 1% 99.5%,
                0% 95%, 0.5% 88%, 0% 81%, 0.4% 74%, 0% 67%, 0.5% 60%, 0% 53%, 0.4% 46%, 0% 39%, 0.5% 32%, 0% 25%, 0.4% 18%, 0% 11%, 0.5% 4%
              )`,
              overflow: "hidden",
            }}>
              {/* Paper grain */}
              <div style={{
                position: "absolute", inset: 0, opacity: 0.18, pointerEvents: "none", mixBlendMode: "multiply",
                backgroundImage: 'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 400 400\'><filter id=\'n\'><feTurbulence type=\'fractalNoise\' baseFrequency=\'0.85\' numOctaves=\'3\' stitchTiles=\'stitch\'/><feColorMatrix values=\'0 0 0 0 0.45  0 0 0 0 0.35  0 0 0 0 0.22  0 0 0 0.5 0\'/></filter><rect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/></svg>")',
                backgroundSize: "300px 300px",
              }} />
              {/* Edge vignette */}
              <div style={{
                position: "absolute", inset: 0, pointerEvents: "none",
                background: "radial-gradient(ellipse 120% 80% at 50% 50%, transparent 55%, rgba(120,90,50,0.12) 95%)",
              }} />

              {/* Close × — inside the paper's upper-right corner */}
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="welcome-dialog-close"
                aria-label="Close"
                style={{
                  position: "absolute",
                  top: "14px",
                  right: "16px",
                  width: "30px",
                  height: "30px",
                  borderRadius: "50%",
                  border: "1px solid rgba(13,35,64,0.2)",
                  background: "rgba(255,255,255,0.4)",
                  color: "#0d2340",
                  fontFamily: "'Playfair Display', serif",
                  fontSize: "16px",
                  lineHeight: "1",
                  cursor: "pointer",
                  zIndex: 5,
                  transition: "background 0.2s ease, transform 0.2s ease",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.7)"; e.currentTarget.style.transform = "rotate(90deg)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.4)"; e.currentTarget.style.transform = "rotate(0deg)"; }}
              >
                ×
              </button>

              {/* HTML content on the paper — padding tuned so the sheet
                  reads as the original portrait-leaning parchment
                  (~485px tall at desktop width 460px), not a short strip. */}
              <div style={{ position: "relative", padding: "56px 42px 52px", zIndex: 1 }}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ ...headingStyle, fontSize: "clamp(10px, 0.95vw, 12px)", opacity: 0.72, marginBottom: "10px", letterSpacing: "4px" }}>
                    Welcome to
                  </div>
                  <h3 id="welcome-dialog-title" style={{ ...headingStyle, fontSize: "clamp(22px, 2.2vw, 28px)", margin: 0 }}>
                    The Farmstand
                  </h3>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "12px", margin: "18px 0 14px" }}>
                    <div style={{ flex: 1, height: "0.5px", background: "linear-gradient(to right, transparent, rgba(13,35,64,0.3), transparent)" }} />
                    <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
                      <path d="M7 1 L8.4 4.3 L7 7.6 L5.6 4.3 Z M7 1 L7 9 M3 5 L11 5" stroke="rgba(13,35,64,0.5)" strokeWidth="0.6" fill="rgba(13,35,64,0.3)" strokeLinecap="round" />
                    </svg>
                    <div style={{ flex: 1, height: "0.5px", background: "linear-gradient(to right, transparent, rgba(13,35,64,0.3), transparent)" }} />
                  </div>
                  <p style={{ ...subtitleStyle, fontSize: "clamp(15px, 1.25vw, 17px)", margin: 0 }}>
                    {user
                      ? "Step inside and look around."
                      : (<>Create your free account,<br />or stay in the know.</>)}
                  </p>
                </div>

                {/* Choice buttons — fully INSIDE the paper with generous
                    bottom padding so nothing floats off the sheet. */}
                <div style={{ marginTop: "44px", display: "flex", flexDirection: "column", gap: "14px" }}>
                  {user ? (
                    <Link
                      ref={firstCtaRef}
                      to="/dashboard"
                      onClick={() => setOpen(false)}
                      data-testid="welcome-cta-explore"
                      style={{
                        display: "block", width: "100%", padding: "14px 0",
                        background: "linear-gradient(180deg, #fcf7e8 0%, #f6efdc 100%)",
                        color: "#0d2340",
                        fontFamily: "'Playfair Display', 'Georgia', serif",
                        fontWeight: 500,
                        fontSize: "12px",
                        letterSpacing: "4px",
                        textTransform: "uppercase",
                        textAlign: "center",
                        textDecoration: "none",
                        border: "1px solid rgba(13,35,64,0.32)",
                      }}
                    >
                      Explore your Farmstand
                    </Link>
                  ) : (
                    <>
                      <Link
                        ref={firstCtaRef}
                        to="/signup"
                        onClick={() => setOpen(false)}
                        data-testid="welcome-cta-signup"
                        style={{
                          display: "block", width: "100%", padding: "14px 0",
                          background: "#0d2340",
                          color: "#f6efdc",
                          fontFamily: "'Playfair Display', 'Georgia', serif",
                          fontWeight: 500,
                          fontSize: "12px",
                          letterSpacing: "4px",
                          textTransform: "uppercase",
                          textAlign: "center",
                          textDecoration: "none",
                          border: "1px solid #0d2340",
                        }}
                      >
                        Create a free account
                      </Link>
                      <button
                        type="button"
                        onClick={() => { setOpen(false); setNotifyOpen(true); }}
                        data-testid="welcome-cta-notify"
                        style={{
                          width: "100%",
                          padding: "14px 0",
                          background: "linear-gradient(180deg, #fcf7e8 0%, #f6efdc 100%)",
                          color: "#0d2340",
                          fontFamily: "'Playfair Display', 'Georgia', serif",
                          fontWeight: 500,
                          fontSize: "12px",
                          letterSpacing: "4px",
                          textTransform: "uppercase",
                          textAlign: "center",
                          border: "1px solid rgba(13,35,64,0.32)",
                          cursor: "pointer",
                        }}
                      >
                        Keep me posted
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reused existing Keep-me-posted (availability) modal — anonymous
          flow posts to /notify/interest. */}
      <NotifyMeModal open={notifyOpen} onClose={() => setNotifyOpen(false)} />
    </>
  );
}

/**
 * WelcomeCard — replaces the previous WaitlistCard. Keeps the SAME folded
 * paper + botanical wax seal visual (owner-supplied blank-paper asset) and
 * overlays real HTML text so the headline reads "WELCOME TO / THE FARMSTAND"
 * and never shows the retired JOIN THE WAITLIST lettering. The parchment is
 * clickable and opens an accessible dialog with TWO choices: create a free
 * account (existing /signup — never paid checkout) or Keep me posted (reuses
 * the existing anonymous availability form via NotifyMeModal). Signed-in
 * visitors see one CTA — "Explore your Farmstand" — deep-linking to /explore.
 */

export default function Home() {
  usePublicPageMeta("/");
  const mapRef = useRef(null);
  const [mapVisible, setMapVisible] = useState(false);
  useEffect(() => {
    const el = mapRef.current;
    if (!el || typeof IntersectionObserver === "undefined") { setMapVisible(true); return; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setMapVisible(true); io.disconnect(); } }, { rootMargin: "600px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-white relative overflow-hidden isolate" data-testid="home-page">
      {/* Map Preview Styles */}
      <style>{`
        .preview-map-container .leaflet-control-zoom {
          display: none !important;
        }
        .preview-map-container .leaflet-control-attribution {
          pointer-events: auto;
          font-size: 10px;
        }
        .preview-map-container {
          pointer-events: none;
        }
        .preview-map-container .leaflet-tile-pane {
          filter: sepia(0.15) saturate(1.1) brightness(1.05);
        }
        .preview-map-container .leaflet-container {
          background: #f5f0e8 !important;
        }
        .preview-map-container .leaflet-tile {
          border: none !important;
          outline: none !important;
        }
        .preview-barn-icon, .preview-carrot-icon {
          background: transparent !important;
          border: none !important;
        }
        .preview-barn-icon img, .preview-carrot-icon img {
          width: 100% !important;
          height: 100% !important;
          object-fit: contain !important;
        }
        .icon-cluster, .icon-cluster-mixed {
          background: transparent !important;
          border: none !important;
        }
        .icon-cluster img, .icon-cluster-mixed img {
          object-fit: contain !important;
        }
        .leaflet-marker-icon {
          background: transparent !important;
          border: none !important;
        }
        .marker-cluster {
          background: transparent !important;
        }
        .marker-cluster div {
          background: transparent !important;
        }
      `}</style>

      {/* Spacer for the fixed header (hero sits directly beneath it) */}
      <div className="w-full pt-[115px] sm:pt-[125px] lg:pt-[140px]" data-testid="home-header-spacer" />

      {/* Hero - Full-bleed luxury ecommerce style */}
      <section 
        className="relative w-screen -ml-[calc((100vw-100%)/2)] overflow-hidden"
        style={{ height: '85vh', minHeight: '500px', maxHeight: '900px' }}
        data-testid="hero-section"
      >
        {/* Full-bleed Hero Image */}
        <img 
          src={HERO_BG} srcSet="/images/optimized/hero-768-v4.webp 768w, /images/optimized/hero-1024-v4.webp 1024w, /images/optimized/hero-v4.webp 1252w" sizes="100vw"
              fetchPriority="high" width="1252" height="2000"
          alt="Girl tending cows beside a barn and hay bales"
          className="absolute inset-0 w-full h-full object-cover object-[center_62%] md:object-[center_42%]"
        />
        
        {/* Dark gradient overlay for text readability */}
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{ 
            background: 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.2) 40%, rgba(0,0,0,0.1) 100%)' 
          }}
        />

        {/* Text and CTA overlay — left-aligned editorial composition (Ralph Lauren style) */}
        <div className="absolute inset-0 flex flex-col justify-end pointer-events-none px-6 sm:px-10 lg:px-16 pb-14 sm:pb-20 lg:pb-24">
          <div className="max-w-2xl pointer-events-auto" style={{ textShadow: '0 2px 14px rgba(0,0,0,0.45)' }}>
            <h1
              className="text-white max-w-xl"
              style={{
                fontFamily: "'GFS Didot', 'Didot', 'Bodoni MT', serif",
                fontSize: 'clamp(1.05rem, 1.4vw, 1.4rem)',
                lineHeight: 1.55,
                letterSpacing: '0.012em',
                fontWeight: 400,
                opacity: 0.95,
                marginBottom: 'clamp(28px, 3vw, 44px)',
              }}
            >
              Direct from independent American farms — bringing you closer to the food that sustains us, and the hands that grow it.
            </h1>
            <div className="flex flex-wrap gap-x-12 gap-y-5">
              <Link to="/for-customers" className="luxe-link-cta" data-testid="hero-cta">
                Explore Now
              </Link>
              <Link to="/for-farms" className="luxe-link-cta" data-testid="hero-cta-farms">
                For Farms
              </Link>
            </div>

            {/* Restrained text link under the two primary CTAs — same
                visual treatment as Explore Now / For Farms (reuses
                luxe-link-cta), on its own second row with ~44px tap
                target. py-3 gives the row 44+px total height while
                luxe-link-cta carries its own 14px underline offset.
                Signed-out visitors go to signup (free account creates
                access to Explore features inside the dashboard);
                signed-in customers go to their dashboard. */}
            <div style={{ marginTop: "20px" }}>
              <Link
                to={user ? "/dashboard" : "/signup?redirect=%2Fdashboard"}
                data-testid="hero-cta-your-farmstand"
                className="luxe-link-cta inline-block py-3"
              >
                {user ? "Your Farmstand" : "Create Your Free Account"}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* What is American Farmstand */}
      <section className="pt-20 sm:pt-28 lg:pt-36 pb-8 lg:pb-12 px-6 lg:px-12" data-testid="about-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <div className="lg:pr-8">
              <h2 
                className="text-2xl lg:text-3xl mb-12 leading-relaxed"
                style={{ color: '#0f1d2f' }}
              >
                Welcome to American Farmstand
              </h2>
              <p 
                className="text-base lg:text-lg leading-loose mb-8"
                style={{ color: '#0f1d2f', opacity: 0.75 }}
              >
                  American Farmstand was created for consumers who care about where their food comes from and how it is grown and raised, and to help independent American farms reach those consumers—both locally and across the nation.
                </p>
              <p 
                className="text-base lg:text-lg leading-loose mb-8"
                style={{ color: '#0f1d2f', opacity: 0.75 }}
              >
                  We bring together farm shopping and farmers market discovery, making exceptional farm products and distinctive regional goods easier to find. From everyday staples to seasonal offerings, the platform provides visibility, clear standards, and direct access to producers—with a closer connection to the origins of what you choose.
                </p>
              <p 
                className="text-base lg:text-lg leading-loose"
                style={{ color: '#0f1d2f', opacity: 0.75 }}
              >
                  Rooted in an appreciation for independent agriculture, our vision is to bring farms from across the United States into one dedicated marketplace, making it more convenient to discover quality at its source. We are building a thoughtfully selected network of independent producers and a community of customers who value quality, provenance, and the opportunity to purchase directly.
                </p>
              <p 
                className="text-base lg:text-lg leading-loose mt-8"
                style={{ color: '#0f1d2f', opacity: 0.75 }}
                data-testid="about-economic-connection-text"
              >
                  By making it easier for customers to purchase directly from independent producers, American Farmstand helps strengthen the economic connection between the people who produce our food and the people who value it—creating more opportunities for farms to reach customers and for consumers to support independent American agriculture.
                </p>
            </div>
            <div className="aspect-[4/5] overflow-hidden">
              <img 
                src={IMAGES.hands} loading="lazy" decoding="async" fetchPriority="low"
                alt="Hands holding wheat at harvest" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Supporting independent farms */}
      <section className="py-28 lg:py-40 px-6 lg:px-12 border-t" style={{ borderColor: 'rgba(30, 58, 95, 0.1)' }} data-testid="support-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <div className="aspect-[4/5] overflow-hidden order-2 lg:order-1">
              <img 
                src={IMAGES.barn} loading="lazy" decoding="async" fetchPriority="low"
                alt="Rustic American barn" 
                className="w-full h-full object-cover"
              />
            </div>
            <div className="lg:pl-8 order-1 lg:order-2">
              <h2 
                className="text-2xl lg:text-3xl mb-12 leading-relaxed"
                style={{ color: '#0f1d2f' }}
              >
                Direct from independent American farms
              </h2>
              <p 
                className="text-base lg:text-lg leading-loose mb-10"
                style={{ color: '#0f1d2f', opacity: 0.75 }}
              >
                  Shop directly with participating farms through one dedicated marketplace, with product details, farm information, and fulfillment options that help you choose with confidence.
                </p>
              <p 
                className="text-base lg:text-lg leading-loose mb-10"
                style={{ color: '#0f1d2f', opacity: 0.75 }}
              >
                  From discovering a nearby market to planning a farm visit or finding a regional offering beyond your usual reach, American Farmstand makes the experience a convenient part of everyday life—with transparency at the center and pickup, local delivery, or shipping wherever offered by participating farms.
                </p>

              <p
                className="text-base lg:text-lg leading-loose"
                style={{ color: '#0f1d2f', opacity: 0.75 }}
              >
                  Our exclusive membership brings personalized discovery and marketplace access together, with member events and exclusive invitations to be announced.
                </p>
            </div>
          </div>
        </div>
      </section>

      {/* Welcome to American Farmstand - Preview Section */}
      <section className="py-24 lg:py-32 px-6 lg:px-12 bg-[#f9f9f8]" data-testid="preview-section">
        <div className="max-w-6xl mx-auto">
          {/* Section Header - Heritage Copy */}
          <div className="text-center mb-16">
            <span 
              className="text-xs tracking-widest uppercase mb-4 block"
              style={{ color: '#B6A46D' }}
            >
              Member Preview
            </span>
            <h2 
              className="text-2xl lg:text-3xl mb-6"
              style={{ color: '#0f1d2f' }}
            >
              Welcome to American Farmstand
            </h2>
            <p 
              className="text-base max-w-xl mx-auto leading-relaxed"
              style={{ color: '#0f1d2f', opacity: 0.65 }}
            >
              Discover farms and farmers markets across the country.{' '}
              <br className="hidden sm:block" />
              Browse seasonal goods, explore the land behind them, and save the places you trust.
            </p>
          </div>

          {/* Preview Cards Grid */}
          <div className="space-y-6">
            {/* Top Row - 3 Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              
              {/* Explore Farms Card - with real farm previews */}
              <div className="relative group">
                <div 
                  className="aspect-[4/5] rounded-lg overflow-hidden relative"
                  style={{ backgroundColor: '#f5f5f5' }}
                >
                  <div className="absolute inset-0 p-3 flex flex-col gap-2" style={{ filter: 'blur(1.5px)' }}>
                    {/* Mock Farm Card 1 */}
                    <div className="flex-1 bg-white rounded-lg shadow-sm overflow-hidden flex flex-col">
                      <div className="h-1/2 relative">
                        <img loading="lazy" fetchPriority="low" src="https://images.pexels.com/photos/1652124/pexels-photo-1652124.jpeg?auto=compress&cs=tinysrgb&w=1600" alt="" className="w-full h-full object-cover" />
                      </div>
                      <div className="p-2 flex-1">
                        <p className="text-[10px] opacity-60" style={{ color: '#0f1d2f' }}>Vermont • Organic Produce</p>
                      </div>
                    </div>
                    {/* Mock Farm Card 2 */}
                    <div className="flex-1 bg-white rounded-lg shadow-sm overflow-hidden flex flex-col">
                      <div className="h-1/2 relative">
                        <img loading="lazy" fetchPriority="low" src="https://images.unsplash.com/photo-1641893638805-5942e0afbec4?crop=entropy&cs=srgb&fm=jpg&q=80&w=900" alt="" className="w-full h-full object-cover" style={{ objectPosition: "center 40%" }} />
                      </div>
                      <div className="p-2 flex-1">
                        <p className="text-[10px] opacity-60" style={{ color: '#0f1d2f' }}>Iowa • Heritage Grains</p>
                      </div>
                    </div>
                    {/* Mock Farm Card 3 */}
                    <div className="flex-1 bg-white rounded-lg shadow-sm overflow-hidden flex flex-col">
                      <div className="h-1/2 relative">
                        <img loading="lazy" fetchPriority="low" src="https://images.pexels.com/photos/35587319/pexels-photo-35587319.jpeg" alt="" className="w-full h-full object-cover" />
                      </div>
                      <div className="p-2 flex-1">
                        <p className="text-[10px] opacity-60" style={{ color: '#0f1d2f' }}>Montana • Grass-fed Beef</p>
                      </div>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0f1d2f]/50 via-[#0f1d2f]/10 to-transparent"></div>
                  <MemberPreviewInvitation />
                  <div className="absolute top-3 right-3 bg-white/90 px-2 py-1 rounded text-xs font-medium" style={{ color: '#0f1d2f' }}>
                    farms
                  </div>
                </div>
                <p className="text-center mt-4 text-sm tracking-wide" style={{ color: '#0f1d2f' }}>
                  Explore Farms
                </p>
              </div>

              {/* Shop Farm Goods Card - with real product previews */}
              <div className="relative group">
                <div 
                  className="aspect-[4/5] rounded-lg overflow-hidden relative"
                  style={{ backgroundColor: '#f5f5f5' }}
                >
                  <div className="absolute inset-0 p-3" style={{ filter: 'blur(1.5px)' }}>
                    <div className="grid grid-cols-2 gap-2 h-full">
                      {/* Product 1 - Honey */}
                      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                        <div className="aspect-square bg-amber-50 flex items-center justify-center">
                          <img loading="lazy" fetchPriority="low" src="https://customer-assets.emergentagent.com/job_farm-direct-20/artifacts/wiz955a2_IMG_1632.jpeg" alt="" className="w-full h-full object-cover" />
                        </div>
                        <div className="p-1.5">
                          <p className="text-[10px] font-medium truncate" style={{ color: '#0f1d2f' }}>Raw Honey</p>
                          <p className="text-[10px] text-green-700">$18.00</p>
                        </div>
                      </div>
                      {/* Product 2 - Vegetables */}
                      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                        <div className="aspect-square">
                          <img loading="lazy" fetchPriority="low" src="https://customer-assets.emergentagent.com/job_farm-direct-20/artifacts/447vyc5c_IMG_1636.jpeg" alt="" className="w-full h-full object-cover" />
                        </div>
                        <div className="p-1.5">
                          <p className="text-[10px] font-medium truncate" style={{ color: '#0f1d2f' }}>Veggie Box</p>
                          <p className="text-[10px] text-green-700">$32.00</p>
                        </div>
                      </div>
                      {/* Product 3 - Eggs */}
                      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                        <div className="aspect-square">
                          <img loading="lazy" fetchPriority="low" src="https://customer-assets.emergentagent.com/job_farm-direct-20/artifacts/i2yy85mr_IMG_1633.jpeg" alt="" className="w-full h-full object-cover" />
                        </div>
                        <div className="p-1.5">
                          <p className="text-[10px] font-medium truncate" style={{ color: '#0f1d2f' }}>Farm Eggs</p>
                          <p className="text-[10px] text-green-700">$8.00</p>
                        </div>
                      </div>
                      {/* Product 4 - Fruits */}
                      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                        <div className="aspect-square">
                          <img loading="lazy" fetchPriority="low" src="https://customer-assets.emergentagent.com/job_farm-direct-20/artifacts/64vehvfl_IMG_1634.jpeg" alt="" className="w-full h-full object-cover" />
                        </div>
                        <div className="p-1.5">
                          <p className="text-[10px] font-medium truncate" style={{ color: '#0f1d2f' }}>Fresh Fruit</p>
                          <p className="text-[10px] text-green-700">$12.00</p>
                        </div>
                      </div>
                      {/* Product 5 - Beef */}
                      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                        <div className="aspect-square">
                          <img loading="lazy" fetchPriority="low" src="https://customer-assets.emergentagent.com/job_farm-direct-20/artifacts/gs98xkm8_IMG_1630.jpeg" alt="" className="w-full h-full object-cover" />
                        </div>
                        <div className="p-1.5">
                          <p className="text-[10px] font-medium truncate" style={{ color: '#0f1d2f' }}>Grass-fed Beef</p>
                          <p className="text-[10px] text-green-700">$24.00</p>
                        </div>
                      </div>
                      {/* Product 6 - Produce */}
                      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                        <div className="aspect-square">
                          <img loading="lazy" fetchPriority="low" src="https://customer-assets.emergentagent.com/job_farm-direct-20/artifacts/tdgdkgbi_IMG_1631.jpeg" alt="" className="w-full h-full object-cover" />
                        </div>
                        <div className="p-1.5">
                          <p className="text-[10px] font-medium truncate" style={{ color: '#0f1d2f' }}>Farm Produce</p>
                          <p className="text-[10px] text-green-700">$9.00</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0f1d2f]/50 via-[#0f1d2f]/10 to-transparent"></div>
                  <MemberPreviewInvitation />
                  <div className="absolute top-3 right-3 bg-white/90 px-2 py-1 rounded text-xs font-medium" style={{ color: '#0f1d2f' }}>
                    products
                  </div>
                </div>
                <p className="text-center mt-4 text-sm tracking-wide" style={{ color: '#0f1d2f' }}>
                  Shop Farm Goods
                </p>
              </div>

              {/* Save Favorites Card - with real saved items */}
              <div className="relative group">
                <div 
                  className="aspect-[4/5] rounded-lg overflow-hidden relative"
                  style={{ backgroundColor: '#f5f5f5' }}
                >
                  <div className="absolute inset-0 p-3 flex flex-col gap-2" style={{ filter: 'blur(1.5px)' }}>
                    {/* Saved Farm 1 */}
                    <div className="flex-1 bg-white rounded-lg shadow-sm overflow-hidden flex">
                      <div className="w-1/3">
                        <img loading="lazy" fetchPriority="low" src="https://images.pexels.com/photos/7658789/pexels-photo-7658789.jpeg" alt="" className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 p-2 flex flex-col justify-between">
                        <div>
                          <p className="text-xs font-medium" style={{ color: '#0f1d2f' }}>Harvest Moon</p>
                          <p className="text-[10px] opacity-60" style={{ color: '#0f1d2f' }}>California</p>
                        </div>
                        <div className="flex justify-end">
                          <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center">
                            <svg className="w-3 h-3 text-red-500" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Saved Farm 2 */}
                    <div className="flex-1 bg-white rounded-lg shadow-sm overflow-hidden flex">
                      <div className="w-1/3">
                        <img loading="lazy" fetchPriority="low" src="https://images.pexels.com/photos/5425794/pexels-photo-5425794.jpeg" alt="" className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 p-2 flex flex-col justify-between">
                        <div>
                          <p className="text-xs font-medium" style={{ color: '#0f1d2f' }}>Green Valley</p>
                          <p className="text-[10px] opacity-60" style={{ color: '#0f1d2f' }}>Oregon</p>
                        </div>
                        <div className="flex justify-end">
                          <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center">
                            <svg className="w-3 h-3 text-red-500" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Saved Farm 3 */}
                    <div className="flex-1 bg-white rounded-lg shadow-sm overflow-hidden flex">
                      <div className="w-1/3">
                        <img loading="lazy" fetchPriority="low" src="https://images.pexels.com/photos/34111852/pexels-photo-34111852.jpeg" alt="" className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 p-2 flex flex-col justify-between">
                        <div>
                          <p className="text-xs font-medium" style={{ color: '#0f1d2f' }}>Blue Ridge</p>
                          <p className="text-[10px] opacity-60" style={{ color: '#0f1d2f' }}>Virginia</p>
                        </div>
                        <div className="flex justify-end">
                          <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center">
                            <svg className="w-3 h-3 text-red-500" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0f1d2f]/50 via-[#0f1d2f]/10 to-transparent"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-12 h-12 rounded-full bg-white/95 flex items-center justify-center shadow-md">
                      <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5" data-testid="save-favorites-preview-heart">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
                      </svg>
                    </div>
                  </div>
                </div>
                <p className="text-center mt-4 text-sm tracking-wide" style={{ color: '#0f1d2f' }}>
                  Save Favorites
                </p>
              </div>
            </div>

            {/* Bottom Row - Map Card Full Width */}
            <div className="relative group">
              <div 
                className="w-full h-[190px] sm:h-auto sm:aspect-[4/1] rounded-lg overflow-hidden relative isolate"
                style={{ backgroundColor: '#dde4d9' }}
              >
                {/* Real Map Preview with Separate Clusters */}
                <div className="absolute inset-0 z-0" ref={mapRef}>
                  <div className="preview-map-container w-full h-full">
                    {mapVisible && (
                      <Suspense fallback={null}>
                        <HomePreviewMap />
                      </Suspense>
                    )}
                  </div>
                </div>
                {/* Legend overlay */}
                <div className="absolute bottom-3 left-3 bg-white/95 px-3 py-2 rounded shadow-sm text-xs" style={{ color: '#0f1d2f' }}>
                  <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1.5">
                      <img src={BARN_ICON_URL} alt="" className="w-4 h-4" style={{ objectFit: 'contain' }} /> 
                      <span>Farms</span>
                    </span>
                    <span className="flex items-center gap-1.5">
                      <img src={CARROT_ICON_URL} alt="" className="w-4 h-4" style={{ objectFit: 'contain' }} /> 
                      <span>Markets</span>
                    </span>
                  </div>
                </div>
              </div>
              <p className="text-center mt-4 text-sm tracking-wide" style={{ color: '#0f1d2f' }}>
                Explore the Land
              </p>
            </div>
          </div>


        </div>
      </section>

      {/* Two paths */}
      <section className="py-20 lg:py-28 px-6 lg:px-12 border-t border-b" style={{ borderColor: 'rgba(30, 58, 95, 0.1)' }}>
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-20 lg:gap-28">
            {/* For Farms */}
            <div className="text-center">
              <span 
                className="text-xs tracking-widest uppercase mb-8 block"
                style={{ color: '#B6A46D' }}
              >
                For Farms
              </span>
              <h3 
                className="text-xl lg:text-2xl mb-6 leading-relaxed"
                style={{ color: '#0f1d2f' }}
              >
                Share what you grow with people who care.
              </h3>
              <p 
                className="text-base leading-loose mb-8"
                style={{ color: '#0f1d2f', opacity: 0.7 }}
                data-testid="for-farms-description"
              >
                Bring your farm to a nationwide community of consumers who value exceptional food, quality, and provenance. Reach customers beyond your usual markets, showcase your products, and make selling more convenient—all while maintaining control over your pricing, products, and fulfillment.
              </p>
              <div className="mb-10 mx-auto max-w-sm pt-6 border-t" style={{ borderColor: 'rgba(182, 164, 109, 0.35)' }}>
                <p
                  className="text-base leading-relaxed italic"
                  style={{ color: '#9A8A55' }}
                  data-testid="for-farms-pricing"
                >
                  Free to join. 0% Farmstand fees for your first year of sales, then just 2%.
                </p>
                <p
                  className="text-xs mt-3 tracking-wide"
                  style={{ color: '#0f1d2f', opacity: 0.5 }}
                  data-testid="for-farms-pricing-note"
                >
                  Stripe payment-processing fees apply separately.
                </p>
              </div>
              <Link 
                to="/apply"
                className="btn-secondary"
                data-testid="apply-cta"
              >
                Apply to Sell
              </Link>
            </div>

            {/* For Members */}
            <div className="text-center">
              <span 
                className="text-xs tracking-widest uppercase mb-8 block"
                style={{ color: '#B6A46D' }}
              >
                For Members
              </span>
              <h3 
                className="text-xl lg:text-2xl mb-6 leading-relaxed"
                style={{ color: '#0f1d2f' }}
              >
                Discover farms you can trust.
              </h3>
              <p 
                className="text-base leading-loose mb-10"
                style={{ color: '#0f1d2f', opacity: 0.7 }}
              >
                This is a space for anyone who values quality, transparency, and supporting American producers. Discover farms, explore their offerings, and support them directly.
              </p>
              <Link 
                to="/subscribe"
                className="btn-secondary"
                data-testid="subscribe-cta"
              >
                Become a Member
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Closing Editorial Image */}
      <section className="relative">
        <div className="aspect-[21/9] lg:aspect-[3/1] overflow-hidden">
          <img 
            src={IMAGES.landscape} loading="lazy" decoding="async"
            alt="Pastoral farmland" 
            className="w-full h-full object-cover"
          />
        </div>
      </section>
    </div>
  );
}
